'use client'

import { useTranslation } from 'react-i18next'
import {
  formatExpiry,
  getCategoryDescriptionKey,
  getCookiePurpose,
  getLifespanKey,
  groupCookiesByCategory,
} from '@/blocks/CookieListBlock/cookieListBlockHelpers'
import { useCookieList } from '@/blocks/CookieListBlock/CookieListProvider'
import * as GridLayout from '@/components/GridLayout/GridLayout'
import { TextWithLink } from '@/components/TextWithLink'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { categoryIntro, table, tableCell, tableHeader, tableNameCell } from './CookieList.css'

const GOOGLE_PRIVACY_POLICY_URL = 'https://business.safety.google/privacy/'

export function CookieList() {
  const { t } = useTranslation('cookieConsent')
  const data = useCookieList()

  if (data == null) {
    return (
      <GridLayout.Root>
        <GridLayout.Content width={{ _: '1', md: '5/6' }} align="center">
          <Text color="textSecondary">{t('COOKIE_LIST_LOAD_ERROR')}</Text>
        </GridLayout.Content>
      </GridLayout.Root>
    )
  }

  if (data.content.length === 0) {
    return null
  }

  const categories = groupCookiesByCategory(data.content)

  return (
    <div className={yStack({ gap: { _: 'lg', md: 'xxl' } })}>
      {categories.map((group) => (
        <section key={group.categoryId} className={yStack({ gap: 'lg', marginBottom: 'lg' })}>
          <GridLayout.Root>
            <GridLayout.Content width={{ _: '1', md: '5/6' }} align={'center'}>
              <div className={yStack({ gap: { _: 'lg', md: 'xxl' } })}>
                <div className={categoryIntro}>
                  <Heading variant={{ _: 'standard.24', md: 'standard.24' }}>{group.categoryName}</Heading>
                  {group.categoryId === 'C0004' ? (
                    <TextWithLink
                      color="textSecondary"
                      href={GOOGLE_PRIVACY_POLICY_URL}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                    >
                      {t(getCategoryDescriptionKey(group.categoryId))}
                    </TextWithLink>
                  ) : (
                    <Text color="textSecondary">{t(getCategoryDescriptionKey(group.categoryId))}</Text>
                  )}
                </div>
                <table className={table}>
                  <thead>
                    <tr>
                      <th className={tableHeader} scope="col">
                        {t('COOKIE_TABLE_NAME')}
                      </th>
                      <th className={tableHeader} scope="col">
                        {t('COOKIE_TABLE_PURPOSE')}
                      </th>
                      <th className={tableHeader} scope="col">
                        {t('COOKIE_TABLE_PROVIDER')}
                      </th>
                      <th className={tableHeader} scope="col">
                        {t('COOKIE_TABLE_SESSION_PERSISTENT')}
                      </th>
                      <th className={tableHeader} scope="col">
                        {t('COOKIE_TABLE_EXPIRY')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((item) => (
                      <tr key={`${item.cookieName}-${item.host}`}>
                        <td className={tableNameCell} title={item.cookieName}>
                          {item.cookieName}
                        </td>
                        <td className={tableCell}>{getCookiePurpose(item)}</td>
                        <td className={tableCell}>
                          {item.thirdParty ? t('COOKIE_PROVIDER_THIRD_PARTY') : t('COOKIE_PROVIDER_FIRST_PARTY')}
                        </td>
                        <td className={tableCell}>{t(getLifespanKey(item.lifespan))}</td>
                        <td className={tableCell}>{formatExpiry(item.expiry, t)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </GridLayout.Content>
          </GridLayout.Root>
        </section>
      ))}
    </div>
  )
}
